// Device chains: what the extension says about a Set's devices, decided
// purely (2026-09-08, slice 34).
//
// The chain's SHAPE is the Set's — the SDK inserts only Live's built-in
// devices, so a browser never adds or removes one. What a poll has to
// decide is whether the Set's chain still matches the room's. Two Sets in
// one room can hold different chains on one lane, and neither receiver can
// reshape its Set to match, so a reshape is asserted only when it GROWS the
// room's chain: the room's is unknown, or empty, or every device the room
// holds is in Live's chain in order (the musician added one). Under
// contention the room's chain then only ever grows, and two Sets can't
// ping-pong it (each asserts only a superset of what the other asserted).
// The price: a device removed in Live stays in the room until a browser or
// a superset assertion replaces the chain. With the same shape, moved
// values travel one knob at a time.
//
// A lane the Set was last made to MATCH is different: when the room's chain
// still has the shape this receiver applied and the Set now differs, the
// difference is the musician's own doing (a device removed, reordered,
// swapped) and the whole chain travels, deletions and all. The jam client
// remembers the chain each `trackDevicesSet` left its Set matching and
// passes `trusted` when the room's chain still has that shape.
//
// A browser can INSERT a device by name (slice 37): it lands in the room as
// a PLACEHOLDER — the name and no parameters — which a receiver turns into
// Live's built-in device of that name. A placeholder matches a real device
// of its name for the growth rule, so the first poll after the insert
// asserts the real chain, parameters and all. A browser can also REMOVE a
// device; a receiver deletes only when its Set matched the room's previous
// chain (the room's edit was of exactly what the Set holds), never a device
// the musician added that the room hasn't caught up with.

import { MAX_DEVICE_NAME_LENGTH, MAX_DEVICE_PARAM_ITEMS, MAX_DEVICE_PARAMS, MAX_DEVICE_PARAMS_PER_LANE, MAX_DEVICES, type DeviceState } from "./state.js";

export interface ParamMove {
  device: number;
  param: number;
  value: number;
}

export type DeviceReconcile =
  | { kind: "chain"; devices: DeviceState[] }
  | { kind: "params"; moves: ParamMove[] };

/** Two chains have the same shape when every device and parameter name lines up by index. */
export function sameChainShape(a: readonly DeviceState[], b: readonly DeviceState[]): boolean {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) if (!sameDeviceShape(a[i]!, b[i]!)) return false;
  return true;
}

function sameDeviceShape(x: DeviceState, y: DeviceState): boolean {
  if (x.name !== y.name || x.params.length !== y.params.length) return false;
  for (let j = 0; j < x.params.length; j++) if (x.params[j]!.name !== y.params[j]!.name) return false;
  return true;
}

/** A placeholder — a name and no parameters — is a browser's insert the Set
 * has yet to fill in. A device the wire's bound truncated is not one. */
export function isPlaceholder(d: DeviceState): boolean {
  return d.params.length === 0 && d.truncated !== true;
}

/** `room`'s device is `live`'s: the same shape, or a placeholder of the same name. */
function roomDeviceMatches(room: DeviceState, live: DeviceState): boolean {
  return isPlaceholder(room) ? room.name === live.name : sameDeviceShape(room, live);
}

/** `room` and `live` line up device for device — a placeholder in `room`
 * matching the real device of its name. What "the Set matched the room"
 * means once a browser's insert has been filled in. */
export function chainMatches(room: readonly DeviceState[], live: readonly DeviceState[]): boolean {
  return room.length === live.length && room.every((d, i) => roomDeviceMatches(d, live[i]!));
}

/** Every device of `room` appears in `live`, in order (as a subsequence, by
 * shape — a placeholder by name). */
export function chainGrows(room: readonly DeviceState[], live: readonly DeviceState[]): boolean {
  let j = 0;
  for (const d of room) {
    while (j < live.length && !roomDeviceMatches(d, live[j]!)) j++;
    if (j >= live.length) return false;
    j++;
  }
  return true;
}

/**
 * A chain cut to the wire's bounds, so the engine never rejects the Set's
 * own chain as `invalid` forever: at most MAX_DEVICES devices, each with at
 * most MAX_DEVICE_PARAMS parameters, MAX_DEVICE_PARAMS_PER_LANE across the
 * lane, names cut to MAX_DEVICE_NAME_LENGTH.
 */
export function boundDevices(devices: readonly DeviceState[]): DeviceState[] {
  const out: DeviceState[] = [];
  let total = 0;
  for (const d of devices.slice(0, MAX_DEVICES)) {
    const room = Math.max(0, Math.min(MAX_DEVICE_PARAMS, MAX_DEVICE_PARAMS_PER_LANE - total));
    const params = d.params.slice(0, room).map((p) => ({
      ...p,
      name: p.name.slice(0, MAX_DEVICE_NAME_LENGTH),
      ...(p.items ? { items: p.items.slice(0, MAX_DEVICE_PARAM_ITEMS).map((s) => s.slice(0, MAX_DEVICE_NAME_LENGTH)) } : {}),
    }));
    total += params.length;
    const truncated = params.length < d.params.length;
    out.push({ name: d.name.slice(0, MAX_DEVICE_NAME_LENGTH), params, ...(truncated ? { truncated: true } : {}), ...(d.truncated ? { truncated: true } : {}) });
  }
  return out;
}

/**
 * What to propose for one lane. `room` undefined means the room has never
 * heard about this lane's chain: assert it — but only when the Set has
 * something to say. An EMPTY Set says nothing about an unknown room: on
 * 2026-10-22 a joiner whose wiped Set held no devices turned "unknown" into
 * "known empty" on twelve lanes (and bumped every rev doing it), so the
 * creator's chains could never be learned. `isEcho(device, param, value)`
 * lets the caller keep its own last writes out of it (the mixer poll's
 * guard).
 *
 * `reshapeNeedsTrust` is the rule for chains that survive a join's wipe —
 * the master's and the returns' (jam-client.ts). A joiner's Set keeps
 * whatever master devices it had, and "the room's chain is inside Live's"
 * is trivially true of a known-empty room chain, which is how Pirojki's own
 * "Acropool - Master Analysis" became Jazzy's master chain. With it set, a
 * KNOWN room chain is reshaped only once this Set was driven to match it
 * (`trusted`); growth alone is not enough.
 */
export function reconcileDevices(
  live: readonly DeviceState[],
  roomChain: readonly DeviceState[] | undefined,
  eps: number,
  isEcho: (device: number, param: number, value: number) => boolean = () => false,
  trusted = false,
  reshapeNeedsTrust = false,
): DeviceReconcile | null {
  const clone = (): DeviceState[] => live.map((d) => ({ name: d.name, ...(d.truncated ? { truncated: true } : {}), params: d.params.map((p) => ({ ...p })) }));
  if (roomChain === undefined) return live.length === 0 ? null : { kind: "chain", devices: clone() };
  // The room's chain on the wire's bounds — a room from before the bounds
  // tightened may hold longer names, which would never compare equal.
  const room = boundDevices(roomChain);
  if (!sameChainShape(live, room)) {
    // A reshape travels whole when this Set was last made to match the
    // room's chain (the difference is the musician's own), else only when
    // it grows the room's chain (see the header); a placeholder filled in
    // by the Set keeps the length and still grows.
    if (trusted) return { kind: "chain", devices: clone() };
    if (reshapeNeedsTrust) return null;
    return chainGrows(room, live) ? { kind: "chain", devices: clone() } : null;
  }
  // A room asserted before value names travelled holds a quantized
  // parameter without its `items`; the Set that has them fills them in
  // whole (one direction only — a Set without names never strips them, so
  // two hosts that disagree cannot ping-pong).
  if (live.some((d, i) => d.params.some((p, j) => (p.items?.length ?? 0) > 0 && !room[i]!.params[j]!.items))) {
    return { kind: "chain", devices: clone() };
  }
  const moves: ParamMove[] = [];
  for (let i = 0; i < live.length; i++) {
    for (let j = 0; j < live[i]!.params.length; j++) {
      const value = live[i]!.params[j]!.value;
      if (Math.abs(value - room[i]!.params[j]!.value) <= eps) continue;
      if (isEcho(i, j, value)) continue;
      moves.push({ device: i, param: j, value });
    }
  }
  return moves.length > 0 ? { kind: "params", moves } : null;
}
